import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useApiToken } from '@/auth/hooks';
import { useOrgMemberStore } from '@/stores/useOrgMemberStore';

const MAX_CURRENT_USER_BOOTSTRAP_ATTEMPTS = 5;
const CURRENT_USER_BOOTSTRAP_RETRY_MS = [0, 150, 300, 600, 1000, 1500];

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider - Wrapper für App-weite Auth-Funktionalität
 * 
 * Hinweis: Die eigentliche MSAL Authentifizierung erfolgt bereits
 * über MsalProvider in main.tsx. Diese Komponente kann für zusätzliche
 * Auth-bezogene Logik verwendet werden, falls benötigt.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const { accounts, inProgress } = useMsal();
  const { getAccessToken } = useApiToken();
  const currentUserId = useOrgMemberStore((state) => state.currentUser?.userId ?? null);
  const loadCurrentUser = useOrgMemberStore((state) => state.loadCurrentUser);
  const clearCurrentUser = useOrgMemberStore((state) => state.clearCurrentUser);
  const hydratedAccountRef = useRef<string | null>(null);

  const activeAccountKey =
    accounts[0]?.homeAccountId ??
    accounts[0]?.localAccountId ??
    accounts[0]?.username ??
    null;

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    if (!activeAccountKey) {
      hydratedAccountRef.current = null;
      clearCurrentUser();
      return;
    }

    if (currentUserId && hydratedAccountRef.current === activeAccountKey) {
      return;
    }

    let cancelled = false;
    let retryTimer: number | undefined;

    const hydrateCurrentUser = async (attempt: number) => {
      const token = await getAccessToken().catch(() => null);
      if (cancelled) {
        return;
      }

      if (!token) {
        if (attempt < MAX_CURRENT_USER_BOOTSTRAP_ATTEMPTS) {
          retryTimer = window.setTimeout(() => {
            void hydrateCurrentUser(attempt + 1);
          }, CURRENT_USER_BOOTSTRAP_RETRY_MS[attempt + 1] ?? 1500);
        }
        return;
      }

      await loadCurrentUser(token);

      if (cancelled) {
        return;
      }

      if (useOrgMemberStore.getState().currentUser) {
        hydratedAccountRef.current = activeAccountKey;
        return;
      }

      if (attempt < MAX_CURRENT_USER_BOOTSTRAP_ATTEMPTS) {
        retryTimer = window.setTimeout(() => {
          void hydrateCurrentUser(attempt + 1);
        }, CURRENT_USER_BOOTSTRAP_RETRY_MS[attempt + 1] ?? 1500);
      }
    };

    void hydrateCurrentUser(0);

    return () => {
      cancelled = true;
      if (retryTimer !== undefined) {
        window.clearTimeout(retryTimer);
      }
    };
  }, [activeAccountKey, clearCurrentUser, currentUserId, getAccessToken, inProgress, loadCurrentUser]);

  return <>{children}</>;
}
